import React, { useState } from 'react';
import {
  AcademicCapIcon,
  BuildingStorefrontIcon,
  CogIcon,
  ArrowRightIcon,
  ComputerDesktopIcon,
  CodeBracketSquareIcon,
  GlobeAltIcon,
  DevicePhoneMobileIcon,
  MagnifyingGlassIcon,
  MegaphoneIcon,
  CursorArrowRaysIcon,
  ChevronDownIcon,
  EnvelopeIcon,
  ChatBubbleLeftEllipsisIcon,
  ShoppingBagIcon,
  PaintBrushIcon,
} from '@heroicons/react/24/outline';
import { useTranslation, Trans } from 'react-i18next';
import { Link } from 'react-router-dom';
import { trackEvent } from '../lib/metaPixel';
import { getCataloguePath } from '../features/training/trainingRoutes';
import { PROJECT_LINKS } from '../lib/projectLinks';

const WHATSAPP_URL = 'https://wa.me/237653459638';
const EMAIL = 'sales@csl-brands.com';

const Services = () => {
  const { t, i18n } = useTranslation();
  const [openAccordion, setOpenAccordion] = useState(null);

  const trainingCataloguePath = getCataloguePath(i18n.language.startsWith('en') ? 'en' : 'fr');

  const toggle = (key) => setOpenAccordion(openAccordion === key ? null : key);

  const products = [
    {
      icon: AcademicCapIcon,
      title: t('services.items.learningPlatform.title'),
      subtitle: t('services.items.learningPlatform.subtitle'),
      status: t('services.items.learningPlatform.status'),
      description: t('services.items.learningPlatform.description'),
      features: t('services.items.learningPlatform.features', { returnObjects: true }),
      color: 'bg-csl-green',
      image: '/CSL-Brands-Learning-Platform.jpg',
      link: PROJECT_LINKS.kursa,
    },
    {
      icon: ComputerDesktopIcon,
      title: t('services.items.bootcamps.title'),
      subtitle: t('services.items.bootcamps.subtitle'),
      status: t('services.items.bootcamps.status'),
      description: t('services.items.bootcamps.description'),
      features: t('services.items.bootcamps.features', { returnObjects: true }),
      color: 'bg-blue-500',
      image: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1471&q=80',
      link: PROJECT_LINKS.bootcamps,
    },
    {
      icon: ShoppingBagIcon,
      title: t('services.items.shopikat.title'),
      subtitle: t('services.items.shopikat.subtitle'),
      status: t('services.items.shopikat.status'),
      description: t('services.items.shopikat.description'),
      features: t('services.items.shopikat.features', { returnObjects: true }),
      color: 'bg-indigo-600',
      image: 'https://images.unsplash.com/photo-1557821552-17105176677c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1471&q=80',
      link: PROJECT_LINKS.shopikat,
    },
    {
      icon: CogIcon,
      title: t('services.items.primecorn.title'),
      subtitle: t('services.items.primecorn.subtitle'),
      status: t('services.items.primecorn.status'),
      description: t('services.items.primecorn.description'),
      features: t('services.items.primecorn.features', { returnObjects: true }),
      color: 'bg-green-600',
      image: 'CSL-Brands-ERP-CRM Platform.jpg',
      link: PROJECT_LINKS.primecorn,
    },
    {
      icon: BuildingStorefrontIcon,
      title: t('services.items.tchopnjoh.title'),
      subtitle: t('services.items.tchopnjoh.subtitle'),
      status: t('services.items.tchopnjoh.status'),
      description: t('services.items.tchopnjoh.description'),
      features: t('services.items.tchopnjoh.features', { returnObjects: true }),
      color: 'bg-orange-500',
      image: '/CSL-Brands-Restaurant-SaaS.jpg',
      link: PROJECT_LINKS.tchopnjoh,
    },
  ];

  const techServices = [
    { key: 'saas', icon: CodeBracketSquareIcon, color: 'bg-violet-500' },
    { key: 'website', icon: GlobeAltIcon, color: 'bg-blue-500' },
    { key: 'mobile', icon: DevicePhoneMobileIcon, color: 'bg-cyan-500' },
    { key: 'branding', icon: PaintBrushIcon, color: 'bg-indigo-500' },
    { key: 'seo', icon: MagnifyingGlassIcon, color: 'bg-amber-500' },
    { key: 'social', icon: MegaphoneIcon, color: 'bg-pink-500' },
    { key: 'ads', icon: CursorArrowRaysIcon, color: 'bg-orange-500' },
    { key: 'training', icon: AcademicCapIcon, color: 'bg-csl-green' },
  ].map(({ key, icon, color }) => ({
    key,
    icon,
    color,
    title: t(`services.techServices.${key}.title`),
    subtitle: t(`services.techServices.${key}.subtitle`),
    description: t(`services.techServices.${key}.description`),
    features: t(`services.techServices.${key}.features`, { returnObjects: true }),
    programs: key === 'training' ? t(`services.techServices.${key}.programs`, { returnObjects: true }) : null,
  }));

  const isAvailable = (status) =>
    ['Available Now', 'Open for Registration', 'Disponible Maintenant', 'Ouvert aux Inscriptions'].includes(status);

  return (
    <section id="services" className="py-12 sm:py-16 lg:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* ── Main heading ───────────────────────────────────────────── */}
        <div className="text-center mb-12 sm:mb-16 animate-slide-up">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
            <Trans i18nKey="services.title">
              What We <span className="text-csl-green">Build & Do</span>
            </Trans>
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto px-4">
            {t('services.description')}
          </p>
        </div>

        {/* ══════════════════════════════════════════════════════════════
            SECTION 1 — TECH PRODUCTS GRID
        ══════════════════════════════════════════════════════════════ */}
        <div className="mb-16 sm:mb-20">
          <div className="flex items-center gap-4 mb-8">
            <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 whitespace-nowrap">
              <Trans i18nKey="services.productsTitle">
                Our <span className="text-csl-green">Products</span>
              </Trans>
            </h3>
            <div className="flex-1 h-px bg-gray-200" />
          </div>
          <p className="text-gray-500 mb-8 -mt-4 text-sm sm:text-base">
            {t('services.productsDescription')}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
            {products.map((product, index) => (
              <div
                key={product.title}
                className="bg-white rounded-xl sm:rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 overflow-hidden group animate-slide-up hover:-translate-y-2"
                style={{ animationDelay: `${index * 150}ms` }}
              >
                {/* Image */}
                <div className="relative h-48 sm:h-56 overflow-hidden">
                  <img
                    src={product.image}
                    alt={product.title}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className={`absolute top-3 right-3 px-2 py-1 rounded-full text-xs font-semibold ${isAvailable(product.status) ? 'bg-csl-green text-white' : 'bg-white/90 text-gray-700'}`}>
                    {product.status}
                  </div>
                  <div className="absolute bottom-3 left-4 text-white">
                    <h4 className="text-xl font-bold mb-0.5">{product.title}</h4>
                    <p className="text-white/85 text-sm font-medium">{product.subtitle}</p>
                  </div>
                </div>

                {/* Content */}
                <div className="p-5 sm:p-6">
                  <div className="flex items-start mb-4">
                    <div className={`w-10 h-10 ${product.color} rounded-xl flex items-center justify-center mr-3 shrink-0`}>
                      <product.icon className="h-5 w-5 text-white" />
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed">{product.description}</p>
                  </div>

                  <ul className="grid grid-cols-2 gap-1.5 mb-5">
                    {(Array.isArray(product.features) ? product.features : []).map((f, i) => (
                      <li key={i} className="flex items-center text-xs text-gray-600">
                        <div className="w-1.5 h-1.5 bg-csl-green rounded-full mr-2 shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>

                  <a
                    href={product.link}
                    target="_blank"
                    rel="noreferrer"
                    className={`w-full flex items-center justify-center px-4 py-2.5 font-semibold rounded-lg transition-all duration-300 text-sm ${product.link !== '#'
                      ? 'bg-csl-green text-white hover:bg-csl-dark-green shadow-md hover:shadow-lg hover:scale-105 active:scale-95'
                      : 'border-2 border-gray-200 text-gray-400 cursor-not-allowed pointer-events-none'}`}
                  >
                    {product.link !== '#' ? t('services.learnMore') : t('services.comingSoon')}
                    {product.link !== '#' && <ArrowRightIcon className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />}
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ══════════════════════════════════════════════════════════════
            SECTION 2 — TECH SERVICES ACCORDION
        ══════════════════════════════════════════════════════════════ */}
        <div>
          <div className="flex items-center gap-4 mb-8">
            <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 whitespace-nowrap">
              <Trans i18nKey="services.servicesTitle">
                Our <span className="text-csl-green">Services</span>
              </Trans>
            </h3>
            <div className="flex-1 h-px bg-gray-200" />
          </div>
          <p className="text-gray-500 mb-8 -mt-4 text-sm sm:text-base">
            {t('services.servicesDescription')}
          </p>

          <div className="space-y-3">
            {techServices.map((svc) => {
              const isOpen = openAccordion === svc.key;
              return (
                <div
                  key={svc.key}
                  className={`border rounded-xl overflow-hidden transition-all duration-200 ${isOpen ? 'border-csl-green shadow-md' : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'}`}
                >
                  {/* Accordion header */}
                  <button
                    onClick={() => toggle(svc.key)}
                    className="w-full flex items-center justify-between px-5 py-4 text-left bg-white hover:bg-gray-50 transition-colors"
                    aria-expanded={isOpen}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 ${svc.color} rounded-lg flex items-center justify-center shrink-0`}>
                        <svc.icon className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <span className="font-semibold text-gray-900 text-sm sm:text-base">{svc.title}</span>
                        <span className="hidden sm:inline text-gray-400 text-sm ml-2">— {svc.subtitle}</span>
                      </div>
                    </div>
                    <ChevronDownIcon
                      className={`h-5 w-5 text-gray-400 transition-transform duration-200 shrink-0 ml-3 ${isOpen ? 'rotate-180 text-csl-green' : ''}`}
                    />
                  </button>

                  {/* Accordion body */}
                  {isOpen && (
                    <div className="px-5 pb-5 pt-1 bg-white border-t border-gray-100">
                      <p className="text-sm sm:text-base text-gray-600 leading-relaxed mb-4">
                        {svc.description}
                      </p>

                      {/* Program cards — training only */}
                      {svc.key === 'training' && Array.isArray(svc.programs) && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
                          {svc.programs.map((prog, i) => (
                            <div key={i} className="rounded-lg border border-csl-green/20 bg-green-50/40 p-4">
                              <p className="font-semibold text-gray-900 text-sm mb-1">{prog.name}</p>
                              <p className="text-xs text-csl-green font-medium mb-2">{prog.duration}</p>
                              <p className="text-xs text-gray-600 leading-relaxed">{prog.detail}</p>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Features — non-training items */}
                      {svc.key !== 'training' && (
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-5">
                          {(Array.isArray(svc.features) ? svc.features : []).map((f, i) => (
                            <li key={i} className="flex items-start text-sm text-gray-700">
                              <div className="w-1.5 h-1.5 bg-csl-green rounded-full mt-1.5 mr-2.5 shrink-0" />
                              {f}
                            </li>
                          ))}
                        </ul>
                      )}

                      {/* CTAs */}
                      <div className="flex flex-col sm:flex-row flex-wrap gap-2 pt-3 border-t border-gray-100">
                        <p className="self-center text-xs font-semibold text-gray-500 uppercase tracking-wider sm:mr-2">
                          {t('services.contactUs')}:
                        </p>
                        <a
                          href={WHATSAPP_URL}
                          target="_blank"
                          rel="noreferrer"
                          onClick={() => trackEvent('Contact', { customData: { content_name: 'whatsapp-cta' } })}
                          className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 transition-colors text-sm font-medium"
                        >
                          <ChatBubbleLeftEllipsisIcon className="h-4 w-4" />
                          {t('services.contactWhatsApp')}
                        </a>
                        <a
                          href={`mailto:${EMAIL}?subject=${encodeURIComponent(svc.title)}`}
                          className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100 transition-colors text-sm font-medium"
                        >
                          <EnvelopeIcon className="h-4 w-4" />
                          {t('services.contactEmail')}
                        </a>
                        {svc.key === 'training' && (
                          <Link
                            to={trainingCataloguePath}
                            className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-csl-green text-white hover:bg-csl-dark-green transition-colors text-sm font-medium shadow-sm"
                          >
                            <ArrowRightIcon className="h-4 w-4" />
                            {t('services.contactBootcamp')}
                          </Link>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

      </div>
    </section>
  );
};

export default Services;
